/**
 * `L4-07` — the Knowledge Base lane's percentage.
 *
 * Omar, 2026-08-21: *"a percentage based on the time that it takes to convert
 * the selected PDF files into synthesised files that will be made available on
 * the KB section"* — then, asked whether he wanted a clock: *"I don't want a
 * time estimate but just a percentage that show the progress."* Then, shown the
 * two ways of computing it side by side: **"go with Option B."**
 *
 * **Why this is not derived from `kbForwardState`.** The tender side's
 * `forwarded` means *the Knowledge Base returned 2xx* — `kb_client.py` treats
 * **202 Accepted** as success and the KB's ingest is an explicit async-job
 * pattern. A percentage built from those states would read **100% the moment
 * the last file was handed over**, while the synthesis that actually makes a
 * document usable is still running. That is
 * `feedback_the_label_is_not_the_write` with a progress bar attached.
 *
 * **Why it matches ids rather than counting (Option B).** The first build
 * counted: it summed progress over everything the KB held for this project and
 * divided by how many the user had approved. That cannot tell *"the document I
 * approved is live"* from *"an unrelated leftover happens to be live"* — so it
 * had to REFUSE whenever the two could differ, and the percentage vanished
 * exactly when someone most wanted it. Each approved document now carries the
 * KB's own id for what it became, so the question is answered by name. The
 * refusal branch is gone: the ambiguity it detected can no longer arise.
 *
 * See `wiki/findings/2026-08-21-kb-lane-percentage.md` for the chain audit.
 */

/** The seven stages `SynthesisStage` defines, in the order the pipeline runs
 *  them. Order is the contract: the index IS the progress. */
export const SYNTHESIS_STAGES = [
  "pending",
  "chunked",
  "embedded",
  "validated",
  "synthesized",
  "edged",
  "done",
] as const;

/**
 * `D-59` — the ladder a LIGHT document climbs.
 *
 * Light-only preparation stops the pipeline after embeddings, so `validated`,
 * `synthesized` and `edged` never arrive. Measured against the seven-stage
 * ladder a light run reads **0 → 14% → 29% → 100%**: it reports three stages
 * that cannot happen as work still outstanding, and then leaps. Omar, 16 Sep
 * §2: *"Remove progress indicators and completion requirements belonging to the
 * disabled KB stages."*
 *
 * A PREFIX of the full ladder plus its terminal, not a separate list — so the
 * two cannot disagree about what a stage NAME means, only about how many there
 * are.
 */
export const LIGHT_SYNTHESIS_STAGES = [
  ...SYNTHESIS_STAGES.slice(0, SYNTHESIS_STAGES.indexOf("validated")),
  "done",
] as const;

/** The ladder this document is on. Absence reads as `full`, which is what a
 *  service build predating light-only preparation actually runs. */
export function stagesFor(mode?: string | null): readonly string[] {
  return mode === "light" ? LIGHT_SYNTHESIS_STAGES : SYNTHESIS_STAGES;
}

export type SynthesisStageName = (typeof SYNTHESIS_STAGES)[number];

/** One document as the Knowledge Base reports it. */
export interface KbProgressDoc {
  id: string;
  status: string;
  /* `undefined` alongside `null` because `KbDoc.synthesisStage` is optional: an
     older KB build omits the field rather than sending null. Both mean "no
     stage reported", and narrowing this would push a cast onto the call site,
     which is how a real absence gets laundered into a value. */
  synthesisStage?: string | null;
  /** `D-59` — `"light"` puts this document on the four-stage ladder. */
  ingestionMode?: string | null;
  /**
   * `KBR-62` — the scan outcome, in the shape `KbDoc` already carries.
   *
   * Declared as the nested object rather than as a flattened `scanState`
   * string, deliberately: `kbQuery.data` is handed to this function DIRECTLY,
   * so a flattened field would need a mapping step at the call site, and a
   * mapping step is a second place for the two shapes to drift. Structural
   * typing makes the narrow shape below satisfied by the real one for free.
   *
   * Counted, never used to move the percentage: whether the check finished is a
   * different question from how far the pipeline got.
   */
  scan?: { state?: string | null } | null;
}

/** One document as the TENDER side knows it — approved, and possibly forwarded. */
export interface ApprovedDoc {
  /** The KB's id for what this became. `null` (or absent) until it is
   *  actually forwarded. */
  kbDocumentId?: string | null;
}

export interface KbProgress {
  /** 0–100, integer. `null` when there is nothing honest to show. */
  percent: number | null;
  /** How many documents the bar is measuring. */
  counted: number;
  /** Reached the Knowledge Base and usable. */
  live: number;
  /** Terminal in the KB — refused or archived. Out of the denominator. */
  excluded: number;
  /**
   * Approved, forwarded, carrying an id — and **absent from what the KB
   * returned**. Should be 0: the KB soft-deletes to `archived` rather than
   * removing rows, so a genuine disappearance means something happened that this
   * build does not model.
   *
   * **Any value above 0 suppresses `percent` entirely.** This field first left
   * the denominator and let the bar carry on, with a comment claiming it was
   * *"counted and reported rather than absorbed"*. An independent tester took
   * that apart and was right on both halves:
   *
   * 1. `excluded` is a **decision** the KB recorded — we know the work will
   *    never finish, and why. `missing` is an **anomaly** — we know nothing.
   *    The row may be mid-write, the walk may have dropped it, the id may be
   *    stale. Treating *"no idea"* as if it were a definite negative is the
   *    exact substitution this whole feature exists to refuse.
   * 2. *"Reported"* was true of this return value and **false of the product**:
   *    `UploadView` reads `percent` and no other field — verified, not assumed.
   *    So the user would have seen **100%**, meaning *"everything you approved
   *    is in the Knowledge Base"*, while a document they approved was
   *    unaccounted for.
   *
   * That was the **fourth** time on this one feature that a comment described a
   * safety the consumer did not honour. The pattern is the lesson: a property of
   * a returned value is not a property of the system until something reads it.
   * So the guarantee now lives in the number itself rather than in a field
   * nobody renders — unknown state, no number.
   */
  missing: number;
  /**
   * `P-80` — the breakdown, because ONE number cannot answer the question the
   * lane is asked.
   *
   * Omar, 12 Sep: *"Show delivery, synthesis generation, checking, recovery,
   * partial checks, awaiting review, availability and failures/exclusions
   * separately. Identify active and queued work. **Awaiting an administrator
   * must not look like an indefinitely running process.**"*
   *
   * That last sentence names a gap this file already admitted in writing, in
   * `documentProgress`'s own comment: a `pending-review` document sits at 6/7
   * exactly like a finished ingest, *"so a document blocked on a human is
   * numerically indistinguishable from one almost finished, and nothing on
   * screen separates them."* It was recorded as a real product gap awaiting a
   * decision. This is the decision.
   *
   * The counts partition `counted` (plus `excluded`, which is outside it), so
   * they can be rendered as a sentence without any of them being derived twice.
   */
  /** Approved and not yet handed over to the Knowledge Base. Real work, queued. */
  queued: number;
  /** Handed over and being read: the pipeline is actively working on these. */
  synthesizing: number;
  /**
   * Waiting for a PERSON, not for a process. A contradiction was raised, or the
   * check could not finish — either way nothing will move until an
   * administrator acts, and the lane must not draw it as work in flight.
   */
  awaitingReview: number;
  /** `KBR-62` — how many of these were checked only partially. Reported beside
   *  the others and deliberately NOT subtracted from anything: an incomplete
   *  check is a fact ABOUT a document, not a state instead of its own. */
  incompleteScan: number;
}

/**
 * **Only `live` is 100%.** An ingesting document tops out at 6/7 even at the
 * `done` stage, because "the pipeline finished its last step" and "the document
 * is available in the Knowledge Base" are different claims and the second is
 * the one this bar makes. A bar reading 100% beside a document nobody can
 * search yet is the defect this whole feature exists to avoid.
 */
function documentProgress(doc: KbProgressDoc): number {
  if (doc.status === "live") return 1;
  if (doc.status === "pending-review") {
    /* Synthesis ran; a contradiction was raised and a human must resolve it
       before the document goes live. Genuinely near the end and genuinely not
       there — the same 6/7 ceiling as a finished ingest.

       KNOWN GAP, stated because this comment once claimed the opposite. It read
       "the lane's own text line is what tells the reader a decision is waiting."
       That was FALSE and an independent designer caught it: the lane's text is
       driven entirely by `kbAwaiting` and `kb.decided` — tender-side forward
       states — and reads this status nowhere. So a document blocked on a human
       is numerically indistinguishable from one almost finished, and nothing on
       screen separates them. A real product gap awaiting a decision, not a
       solved problem (`feedback_naming_the_mechanism_is_a_claim`). */
    return (SYNTHESIS_STAGES.length - 1) / SYNTHESIS_STAGES.length;
  }
  const ladder = stagesFor(doc.ingestionMode);
  const i = ladder.indexOf(doc.synthesisStage ?? "pending");
  /* An unrecognised stage reads as 0 rather than as an error. The KB may add a
     stage before this list learns about it, and a new stage arriving must slow
     the bar down, never crash the screen or invent progress. */
  return (i < 0 ? 0 : i) / ladder.length;
}

const TERMINAL = new Set(["rejected", "archived"]);

/**
 * @param approved the documents the user approved for the Knowledge Base — the
 *                 tender side's `queued` + `forwarding` + `forwarded`. This is
 *                 the denominator, NOT `kbDocs.length`: a document said yes to
 *                 but not yet handed over is real work not yet done, and
 *                 counting only what arrived would show 100% with one of ten in.
 * @param kbDocs   what the Knowledge Base holds for this project. Anything here
 *                 that no approved document points at is IGNORED — that is the
 *                 whole of Option B, and the reason no refusal is needed.
 */
export function kbSynthesisProgress(approved: ApprovedDoc[], kbDocs: KbProgressDoc[]): KbProgress {
  if (approved.length === 0) {
    return {
      percent: null,
      counted: 0,
      live: 0,
      excluded: 0,
      missing: 0,
      queued: 0,
      synthesizing: 0,
      awaitingReview: 0,
      incompleteScan: 0,
    };
  }

  const byId = new Map(kbDocs.map((d) => [d.id, d]));

  let sum = 0;
  let counted = 0;
  let live = 0;
  let excluded = 0;
  let missing = 0;
  let queued = 0;
  let synthesizing = 0;
  let awaitingReview = 0;
  let incompleteScan = 0;

  for (const doc of approved) {
    /* `== null`, deliberately loose: it catches `undefined` as well as `null`.
       A caller that omits the field entirely means the same thing as one that
       sends null — "not forwarded" — and the strict form would instead look up
       `undefined` in the map, miss, and count the document as MISSING. The same
       trap `synthesisStage?: string | null` above exists to avoid. */
    if (doc.kbDocumentId == null) {
      /* Approved and not yet handed over. Real work, not yet done: it counts in
         the denominator and contributes nothing. */
      counted += 1;
      queued += 1;
      continue;
    }
    const kb = byId.get(doc.kbDocumentId);
    if (kb === undefined) {
      missing += 1;
      continue;
    }
    if (TERMINAL.has(kb.status)) {
      /* Terminal, and out of the denominator: keeping it would pin the bar
         below 100% forever with no action anyone could take to move it. What it
         must NOT do is vanish silently — the lane prints the skipped and failed
         counts beside this, and `excluded` is returned. */
      excluded += 1;
      continue;
    }
    counted += 1;
    sum += documentProgress(kb);
    if (kb.status === "live") live += 1;
    else if (kb.status === "pending-review") awaitingReview += 1;
    else synthesizing += 1;
    /* Counted for EVERY non-terminal document, live included. Two of the first
       real tender's documents were `live` AND partially checked, and that pair
       is the whole reason `KBR-62` exists: «available» and «fully checked» are
       different facts and the lane has to be able to say both. */
    if (kb.scan?.state === "incomplete") incompleteScan += 1;
  }

  /* An anomaly suppresses the number outright. See `missing` above: we do not
     know what happened to those documents, and a bar that reads 100% while an
     approved document is unaccounted for is a confident claim over an unknown.
     On the reasoning that `missing` should be structurally unreachable, this
     condition should never fire — which is the best kind of guard to have. */
  const breakdown = { queued, synthesizing, awaitingReview, incompleteScan };
  if (missing > 0) return { percent: null, counted, live, excluded, missing, ...breakdown };

  if (counted === 0) return { percent: null, counted: 0, live, excluded, missing, ...breakdown };

  return {
    /* Floored, never rounded up: 99.6% must not print as 100% while a document
       is still being synthesised. The only route to 100 is every counted
       document being `live`. */
    percent: Math.floor((sum / counted) * 100),
    counted,
    live,
    excluded,
    missing,
    ...breakdown,
  };
}

/**
 * What the lane shows when there is no *successfully loaded* answer to show —
 * loading, errored, truncated, disabled, or any state added later.
 *
 * **This is a mechanism replacement, not a patch.** Three times on this one
 * feature a guard was written together with a comment asserting what it
 * achieved, and the comment was wrong because the consumer was never checked.
 * The last was the truncation throw: `kbApi.ts` claimed *"the lane shows nothing
 * rather than a wrong number — LOUD beats plausible"*, while `UploadView` read
 * the hook as `.data ?? []`, so an errored query was byte-identical to a loading
 * one and the lane printed a confident **0%** over an unknown fraction. An
 * independent tester measured it; no product test could, because the claim lived
 * in a comment.
 *
 * So the consumer no longer ENUMERATES the bad states — it REQUIRES the good
 * one. A percentage is computed only from a query that actually succeeded;
 * everything else lands here. A new failure state cannot be forgotten, because
 * nothing has to remember it.
 */
export const KB_PROGRESS_UNAVAILABLE: KbProgress = {
  percent: null,
  counted: 0,
  live: 0,
  excluded: 0,
  missing: 0,
  queued: 0,
  synthesizing: 0,
  awaitingReview: 0,
  incompleteScan: 0,
};
